<script setup lang="ts">
import {
    VBtn,
    VIcon,
    VMenu,
    VList,
    VListItem,
    VTooltip,
} from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/utils/date';
import type { BottleneckItem } from './BottleneckTable.vue';
import BottleneckDeleteDialog from './BottleneckDeleteDialog.vue';
import BottleneckEditModal from './BottleneckEditModal.vue';
import BottleneckEscalateModal from './BottleneckEscalateModal.vue';
import BottleneckStatusComponent from './BottleneckStatus.vue';
import BottleneckSolveModal from './BottleneckSolveModal.vue';
import EscalationUpdateModal from './EscalationUpdateModal.vue';
import { computed, ref } from 'vue';
import { BottleneckStatus } from '@/gql/graphql';
import { useRouteQuery } from '@vueuse/router';
import { FormName, useFormSession } from '@/composables/form-session';

const {
    item,
    canDelete,
    canUpdate,
    canEscalate,
    columns = undefined,
    isExpanded = true,
} = defineProps<{
    item: BottleneckItem;
    canDelete: boolean;
    canUpdate: boolean;
    canEscalate: boolean;
    columns?: string[];
    isExpanded?: boolean;
}>();

const emit = defineEmits<{
    (e: 'toggle-expand'): void;
}>();
const { t } = useI18n({
    messages: {
        nl: {
            edit: 'Bewerken',
            delete: 'Verwijderen',
            escalate: 'Escaleer',
            solve: 'Oplossen',
            escalation_blocking: 'Los de escalatie eerst op',
            delete_escalation: 'Escalatie verwijderen',
        },
        en: {
            edit: 'Edit',
            delete: 'Delete',
            escalate: 'Escalate',
            solve: 'Solve',
            escalation_blocking: 'Solve escalation first',
            delete_escalation: 'Delete escalation',
        },
    },
});

const formSession = useFormSession();

const isShowingSolveModal = ref(false);
const isSolvingEscalation = ref(false);
const isShowingEscalateModal = ref(false);

function handleSolveBottleneck() {
    isSolvingEscalation.value = false;
    isShowingSolveModal.value = true;
}

const canUpdateBottleneck = computed(() => {
    return (
        canUpdate &&
        (item.status !== BottleneckStatus.ESCALATION || canEscalate)
    );
});

const canDeleteBottleneck = computed(() => {
    return (
        canDelete &&
        (item.status !== BottleneckStatus.ESCALATION || canEscalate)
    );
});

const formQuery = useRouteQuery('form');
const formIdQuery = useRouteQuery('id');

const isShowingEditModal = ref(false);
const isShowingEscalationEditModal = ref(false);

formSession.onResult(() => {
    if (formQuery.value && formIdQuery.value) {
        if (formIdQuery.value !== item.id) return;
        if (!formSession.session.value?.fields) return;

        switch (formQuery.value) {
            case FormName.bottleneck_edit:
                isShowingEditModal.value = true;
                break;
            case FormName.escalation_edit:
                isShowingEscalationEditModal.value = true;
                break;
            case FormName.bottleneck_escalate:
                isShowingEscalateModal.value = true;
                break;
        }
    }
});
</script>

<template>
    <BottleneckSolveModal
        v-model="isShowingSolveModal"
        :bottleneck="item"
        :solving-escalation="isSolvingEscalation"
    />
    <BottleneckEscalateModal
        v-model="isShowingEscalateModal"
        :bottleneck="item"
    />
    <BottleneckEditModal
        v-if="item.status === 'bottleneck'"
        v-model="isShowingEditModal"
        :bottleneck="item"
    />
    <EscalationUpdateModal
        v-if="item.status === 'escalation'"
        v-model="isShowingEscalationEditModal"
        :bottleneck="item as Required<BottleneckItem>"
    />
    <tr>
        <td
            v-if="!columns || columns?.includes('status')"
            class="v-data-table__td"
        >
            <BottleneckStatusComponent :status="item.status" />
        </td>
        <td
            v-if="!columns || (columns && columns.includes('name'))"
            class="v-data-table__td"
        >
            <span class="text-no-wrap">{{ item.name }}</span>
        </td>
        <td
            v-if="!columns || columns?.includes('description')"
            class="v-data-table__td"
        >
            <div class="bottleneck-table__text py-2">
                {{ item.description || '-' }}
            </div>
        </td>
        <td
            v-if="!columns || columns?.includes('solution')"
            class="v-data-table__td"
        >
            <div class="bottleneck-table__text py-2">
                {{ item.solution || '-' }}
            </div>
        </td>
        <td
            v-if="!columns || columns?.includes('resolutionDate')"
            class="v-data-table__td"
        >
            {{ item.resolutionDate ? formatDate(item.resolutionDate) : '-' }}
        </td>
        <td class="v-data-table__td">
            <v-btn
                v-if="item.escalations?.length"
                variant="text"
                color="neutral"
                class="text-label-small"
                @click="emit('toggle-expand')"
            >
                {{ item.escalations.length }}
                <v-icon>{{
                    isExpanded ? 'expand_less' : 'expand_more'
                }}</v-icon>
            </v-btn>
        </td>
        <td class="v-data-table__td">
            <div class="d-flex justify-end">
                <v-menu
                    v-if="
                        (canEscalate || canUpdateBottleneck) &&
                        item.status !== BottleneckStatus.SOLVED
                    "
                    :close-on-content-click="true"
                >
                    <template #activator="{ props: activatorProps }">
                        <v-btn-icon
                            size="x-small"
                            icon="$more"
                            v-bind="activatorProps"
                        />
                    </template>
                    <v-list>
                        <v-list-item
                            v-if="canEscalate"
                            :disabled="
                                item.status !== BottleneckStatus.BOTTLENECK ||
                                item.hasActiveEscalation
                            "
                            @click="isShowingEscalateModal = true"
                        >
                            {{ t('escalate') }}
                        </v-list-item>
                        <VTooltip
                            v-if="canUpdateBottleneck"
                            :disabled="!item.hasActiveEscalation"
                            :text="t('escalation_blocking')"
                            location="left"
                        >
                            <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps">
                                    <v-list-item
                                        :disabled="item.hasActiveEscalation"
                                        @click="
                                            !item.hasActiveEscalation &&
                                            handleSolveBottleneck()
                                        "
                                    >
                                        {{ t('solve') }}
                                    </v-list-item>
                                </span>
                            </template>
                        </VTooltip>
                        <VTooltip
                            v-if="canUpdateBottleneck"
                            :disabled="!item.hasActiveEscalation"
                            :text="t('escalation_blocking')"
                            location="left"
                        >
                            <template #activator="{ props: tooltipProps }">
                                <span v-bind="tooltipProps">
                                    <v-list-item
                                        :disabled="item.hasActiveEscalation"
                                        @click="
                                            !item.hasActiveEscalation &&
                                            (isShowingEditModal = true)
                                        "
                                    >
                                        {{ t('edit') }}
                                    </v-list-item>
                                </span>
                            </template>
                        </VTooltip>
                    </v-list>
                </v-menu>
                <BottleneckDeleteDialog
                    v-if="canDeleteBottleneck"
                    :bottleneck-id="item.id"
                >
                    <template
                        #activator="{
                            props: activatorProps,
                            loading: deleting,
                        }"
                    >
                        <v-btn-icon
                            size="x-small"
                            icon="$delete"
                            :aria-label="t('delete')"
                            :loading="deleting"
                            v-bind="activatorProps"
                        />
                    </template>
                </BottleneckDeleteDialog>
            </div>
        </td>
    </tr>
</template>
